import { IHashMap, isEmptyInput, isNullOrUndefined } from '@adaptiva/core/common';
import { BaseEditorError, EEditorErrorType, IEditorInput } from '@adaptiva/data-service';
import { ContentOverrideForRepairProperties, ContentOverrideForUninstallationProperties, ContentProperties, EFileType, ESourceType, ICdnFile, MasCdnUploadService, MetadataObject, MetadataSerialization } from '@adaptiva/metadata/common';
import { AfterViewInit, Directive, OnDestroy, OnInit, inject } from '@angular/core';
import { isEqual } from 'lodash';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { BaseMetadataEditorComponent } from '../base-metadata-editor';

type ComparableUploadProperties = Pick<ContentProperties, 'AdaptivaUrl' | 'SourceType' | 'VendorUrl' | 'FileName'>;

@Directive()
export abstract class BaseUploadableMetadataEditorComponent<T extends MetadataObject>
	extends BaseMetadataEditorComponent<T> implements OnInit, OnDestroy, AfterViewInit {

	public cdnUploadOptionsInstallation: BehaviorSubject<ICdnFile> = new BehaviorSubject(null);

	public cdnUploadOptionsRepair: BehaviorSubject<ICdnFile> = new BehaviorSubject(null);

	public cdnUploadOptionsUninstallation: BehaviorSubject<ICdnFile> = new BehaviorSubject(null);

	protected masCdn: MasCdnUploadService = inject(MasCdnUploadService);

	public saveMetadataObject(): void {
		this.cdnUploadOptionsInstallation.pipe(
			take(1),
			tap(() => this.canEdit$.next(false)),
			switchMap((_installationContent$: ICdnFile) => {
				return this._checkAndUploadContent(_installationContent$, ContentProperties.namespace);
			}),
			switchMap(() => this.cdnUploadOptionsRepair),
			take(1),
			switchMap((_repairContent$: ICdnFile) => {
				return this._checkAndUploadContent(_repairContent$, ContentOverrideForRepairProperties.namespace);
			}),
			switchMap(() => this.cdnUploadOptionsUninstallation),
			take(1),
			switchMap((_uninstallationContent$: ICdnFile) => {
				return this._checkAndUploadContent(_uninstallationContent$, ContentOverrideForUninstallationProperties.namespace);
			}),
			take(1)
		).subscribe({
			next: () => {
				super.saveMetadataObject();
				this.canEdit$.next(true);
			},
			error: (err: Error) => {
				this.overlayService.error('Error uploading files', err.message);
				this.canEdit$.next(true);
			}
		});
	}

	private _checkAndUploadContent(_content: ICdnFile, _namespace: string): Observable<boolean> {
		if (!this.input.objectId)
			return of(false);

		const _current: ComparableUploadProperties = this._getContentUploadProperties(this.state.value, _namespace);
		const _saved: ComparableUploadProperties = this._getContentUploadProperties(this.state.lastSaved, _namespace);
		const _fileType: EFileType = this._getFileTypeFromNameSpace(_namespace);

		if (isEmptyInput(_content) && isEmptyInput(_current.VendorUrl))
			return of(false);

		// check if upload-related properties have not changed since last save
		if (isEqual(_current, _saved))
			return of(false);
		else if (
			isEmptyInput(_current.VendorUrl) ||
			isEmptyInput(_current.AdaptivaUrl) ||
			isEmptyInput(_content)
		) {
			if (isEmptyInput(_current.FileName))
				return throwError(new Error('A user-defined FileName is required in ' + _namespace));
		}

		// Prep and handle metadata values, then send to upload service
		if (_current.SourceType === ESourceType.ADAPTIVA_CDN || _current.SourceType === ESourceType.VENDOR_CDN_WITH_BACKUP) {
			if (_current.SourceType === ESourceType.ADAPTIVA_CDN) {
				MetadataSerialization.removePropertyValue<ContentProperties>(
					this.state.value, _namespace, 'AdaptivaUrl'
				);
			}

			return this._uploadContent(_content, _current.VendorUrl, _namespace, _fileType).pipe(
				tap(() => {
					if (_current.SourceType === ESourceType.ADAPTIVA_CDN) {
						MetadataSerialization.removePropertyValue<ContentProperties>(
							this.state.value, _namespace, 'VendorUrl'
						);
					}
				})
			);
		} else if (_current.SourceType === ESourceType.VENDOR_CDN) {
			MetadataSerialization.removePropertyValue<ContentProperties>(
				this.state.value, _namespace, 'AdaptivaUrl'
			);
			return of(false);
		} else
			return of(false);
	}

	private _uploadContent(_content: ICdnFile, _uploadUrl?: string, _namespace?: string, _fileType?: EFileType): Observable<boolean> {
		if (isNullOrUndefined(_content) && isNullOrUndefined(_uploadUrl))
			return of(false); // No content to upload

		const _fileName: string = MetadataSerialization.getPropertyValue<string, ContentProperties>(this.state.value, _namespace, 'FileName');

		return this.globalEditorService.editorInputChange.pipe(
			take(1),
			switchMap((_input: IEditorInput<T>) => {
				const _cso: MetadataObject = this.state.value;
				const _vendorUrl: string = MetadataSerialization.getPropertyValue<string, ContentProperties>(_cso, _namespace, 'VendorUrl');

				if (!isNullOrUndefined(_content))
					return this.masCdn.upload(_cso.objectId, _content.file, _content.fileType, _fileName);

				return this.masCdn.uploadFromUrl(_cso.objectId, _fileName, _vendorUrl, _fileType);
			}),
			map((_url: string) => {
				if (_url) {
					const _message: string = `Finished Uploading ${_content?.fileName || _fileName} to CDN`;
					MetadataSerialization.setPropertyValue(this.state.value, _namespace, 'AdaptivaUrl', _url);

					this.snackbarService.display({ message: _message, dismissable: true, duration: 3000 });

					return true;
				} else {
					this.logger.error('BaseConentAuthoringProperties.uploadToCdn', 'User Cancelled Upload');
					this.restorePreviousUrl(_namespace);

					return false;
				}
			}),
			catchError((_err: any) => {
				const _errorMessage: string = this.logger.extractErrorMessage(_err);
				this.logger.error('BaseConentAuthoringProperties.uploadToCdn', _errorMessage);
				this.restorePreviousUrl(_namespace);
				return throwError(_err);
			})
		);
	}

	public restorePreviousUrl(_namespace: string): void {
		this.canEdit$.next(true);
		const _previousUrl: string = MetadataSerialization.getPropertyValue<string, ContentProperties>(
			this.state.lastSaved, _namespace, 'AdaptivaUrl'
		);

		this.state.update(this.targetPropertyMap, (_currentMap: IHashMap<any>) => {
			_currentMap[_namespace]['AdaptivaUrl'] = _previousUrl;
			return _currentMap;
		});
		this.clearUploadSubjects();
	}

	private _getFileTypeFromNameSpace(_namespace: string): EFileType {
		if (_namespace === ContentProperties.namespace)
			return EFileType.INSTALL;
		else if (_namespace === ContentOverrideForRepairProperties.namespace)
			return EFileType.REPAIR;
		else if (_namespace === ContentOverrideForUninstallationProperties.namespace)
			return EFileType.UNINSTALL;
	}

	private _getContentUploadProperties(_object: MetadataObject, _namespace: string): ComparableUploadProperties {
		return {
			AdaptivaUrl: MetadataSerialization.getPropertyValue<string, ContentProperties>(_object, _namespace, 'AdaptivaUrl'),
			FileName: MetadataSerialization.getPropertyValue<string, ContentProperties>(_object, _namespace, 'FileName'),
			SourceType: MetadataSerialization.getPropertyValue<ESourceType, ContentProperties>(_object, _namespace, 'SourceType'),
			VendorUrl: MetadataSerialization.getPropertyValue<string, ContentProperties>(_object, _namespace, 'VendorUrl'),
		};
	}

	public validate(): BaseEditorError[] {
		const _errors: BaseEditorError[] = [];

		if (!this.state.value.name)
			_errors.push(new BaseEditorError(EEditorErrorType.Error, 0, ['Name', this.className + ' Name required']));

		return _errors;
	}
}
